<template>
    <div class="py-10">
        <div v-if="loading" class="normal-container">
            <div class="mx-auto max-w-screen-2xl animate-pulse text-center">
                <div class="px-10 lg:px-20">
                    <div class="looks-sanity-content lg:flex lg:justify-center">
                        <div class="look-content h-40 rounded bg-gray-200 lg:w-2/5"></div>
                    </div>
                </div>
                <div class="mb-10 mt-10 flex flex-wrap justify-center gap-x-8 px-5 lg:mb-20 lg:px-0">
                    <div class="h-6 w-20 rounded-full bg-gray-200"></div>
                    <div class="mx-2 h-6 w-20 rounded-full bg-gray-200"></div>
                    <div class="h-6 w-20 rounded-full bg-gray-200"></div>
                </div>

                <div
                    class="mx-auto grid grid-cols-2 gap-4 px-10 transition-all duration-500 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-10">
                    <div class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                        <div class="h-full w-full rounded bg-gray-200"></div>
                    </div>
                    <div class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                        <div class="h-full w-full rounded bg-gray-200"></div>
                    </div>
                    <div class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                        <div class="h-full w-full rounded bg-gray-200"></div>
                    </div>
                </div>

                <div
                    class="mx-12 mt-6 flex justify-center gap-x-8 border-b-2 border-t-2 border-green-550 py-2 pt-2.5 lg:gap-x-6">
                    <div class="h-6 w-6 rounded-full bg-gray-200"></div>
                    <div class="mx-2 h-6 w-6 rounded-full bg-gray-200"></div>
                    <div class="h-6 w-6 rounded-full bg-gray-200"></div>
                </div>
            </div>
        </div>
        <div v-else class="w-full text-center">
            <div class="looks-sanity-content lg:flex lg:justify-center">
                <div class="sanity-content lg:w-2/5">
                    <SanityContent :blocks="body" />
                </div>
            </div>
            <div class="mb-10 mt-3 flex flex-wrap justify-center gap-x-8 font-monument-extended lg:mb-20 lg:mt-10">
                <button
                    @click="updateGender('')"
                    :class="{
                        'border-b-2 border-green-600 pb-1 !text-black': selectedGender === '',
                    }"
                    class="!w-max text-sm text-blg-gray-200 hover:border-b-2 hover:text-green-600 lg:text-lg">
                    {{ snippets['headless.looks.everything'] }}
                </button>
                <button
                    @click="updateGender('dames')"
                    :class="{
                        'border-b-2 border-green-600 pb-1 !text-black': selectedGender == 'dames',
                    }"
                    class="!w-max pb-1 text-sm text-blg-gray-200 hover:border-b-2 hover:text-green-600 lg:text-lg">
                    {{ snippets['headless.looks.ladies'] }}
                </button>
                <button
                    @click="updateGender('heren')"
                    :class="{
                        'border-b-2 border-green-600 pb-1 !text-black': selectedGender == 'heren',
                    }"
                    class="!w-max pb-1 text-sm text-blg-gray-200 hover:border-b-2 hover:text-green-600 lg:text-lg">
                    {{ snippets['headless.looks.men'] }}
                </button>
            </div>

            <div
                class="normal-container grid grid-cols-2 gap-4 transition-all duration-500 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-10">
                <div
                    v-for="look in looks"
                    :key="look.versionId"
                    class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                    <LookElement :look="look" />
                </div>
            </div>

            <div
                v-if="totalPages >= 2"
                class="normal-container mt-6 flex justify-center gap-x-8 border-b-2 border-t-2 border-green-550 py-2 pt-2.5 lg:gap-x-6">
                <button
                    v-if="totalPages > 1"
                    class="mx-1 bg-green-200 px-2.5 py-2.5 lg:bg-transparent lg:px-4 lg:py-1"
                    :disabled="currentPage === 1"
                    @click="previousPage">
                    <NuxtImg class="size-3 rotate-180 lg:size-5" src="/icons/Arrow_right_black.svg" />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    class="mx-1 px-0.5 text-base text-black"
                    :class="{
                        'border-b-2 border-black text-black': currentPage === page,
                    }">
                    {{ page }}
                </button>
                <button
                    v-if="totalPages > 1"
                    class="mx-1 bg-green-200 px-2.5 py-2.5 lg:bg-transparent lg:px-4 lg:py-1"
                    :disabled="currentPage === totalPages"
                    @click="nextPage">
                    <NuxtImg class="size-3 lg:size-5" src="/icons/Arrow_right_black.svg" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    const props = defineProps({
        body: {
            type: Array,
            required: true,
        },
    });

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const selectedGender = ref('');
    const itemsPerPage = ref(12);
    const loading = ref(true);
    const currentPage = ref(1);
    const looks = ref([]);
    const pagination = ref({});
    const totalPages = ref(1);

    const fetchLooks = async () => {
        const { data, error } = await useFetch('/api/looks/getAllLooks', {
            method: 'POST',
            body: JSON.stringify({
                gender: selectedGender.value,
                page: currentPage.value,
                itemsPerPage: itemsPerPage.value,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (error.value) {
            return;
        }

        if (data.value) {
            looks.value = data.value.looksObject || [];
            pagination.value = data.value.pagination || {};
            totalPages.value = data.value.pagination?.totalPages || 1;
        }
        loading.value = false;
    };

    const updateGender = (gender: string) => {
        selectedGender.value = gender;
        currentPage.value = 1;
        fetchLooks();
    };

    const previousPage = () => {
        if (currentPage.value > 1) {
            currentPage.value -= 1;
            goUp();
            fetchLooks();
        }
    };

    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            currentPage.value += 1;
            goUp();
            fetchLooks();
        }
    };

    const goToPage = (page: number) => {
        currentPage.value = page;
        goUp();
        fetchLooks();
    };

    watch(
        [selectedGender, itemsPerPage, currentPage],
        () => {
            fetchLooks();
        },
        { immediate: true },
    );

    const formattedSeoUrl = (url: string) => {
        if (!url) return '';
        const segments = url.split('/');
        return `/look/${segments[segments.length - 1]}`;
    };

    function goUp() {
        const element = document.querySelector('.looks-sanity-content');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }
</script>

<style scoped>
    .sanity-content p {
        margin-top: 40px !important;
    }

    @media (max-width: 500px) {
        .sanity-content p {
            margin-top: 12px !important;
        }
    }
</style>
